// src/components/simulation/WhatsAppSimulator.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Shield, AlertTriangle, CheckCircle, Search, Camera, MoreVertical, Send, Check, X, BookOpen } from 'lucide-react';

// Default fallback scenario
const getDefaultScenario = () => ({
  title: "Suspicious WhatsApp Message",
  description: "You receive a WhatsApp message from an unknown number claiming to be from your bank.",
  initialSceneId: 'start',
  arenaId: 'phishing_social_engineering',
  scenes: {
    'start': {
      speaker: 'Bank Security',
      profilePic: 'Bank',
      line: "URGENT: We have detected suspicious activity on your account. Please provide your 6-digit verification code to secure your account immediately.",
      choices: [
        { text: "Here is the code: 482910", nextSceneId: 'fail_compliance' },
        { text: "I will not share a code. How do I know you're legitimate?", nextSceneId: 'correct_verify' }
      ]
    },
    'fail_compliance': {
      speaker: 'Bank Security',
      profilePic: 'Bank',
      line: "Thank you for verifying. Your account is now secure.",
      isEndState: true,
      isCorrect: false,
      explanation: "You should never share verification codes. Banks and other legitimate services will never ask for them via chat.",
      indicators: ["Urgent language", "Request for verification code", "Unsolicited contact"]
    },
    'correct_verify': {
      speaker: 'Bank Security',
      profilePic: 'Bank',
      line: "This is a standard procedure. If you do not cooperate we will have to freeze your account.",
      isEndState: true,
      isCorrect: true,
      explanation: "You correctly identified the suspicious request and questioned the sender's identity."
    }
  }
});

const WhatsAppSimulator = ({ scenarios = [], onComplete, challenge }) => {
  const [view, setView] = useState('chatList');
  const [scenario, setScenario] = useState(null);
  const [history, setHistory] = useState([]);
  const [currentScene, setCurrentScene] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [finalFeedback, setFinalFeedback] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const chatEndRef = useRef(null);
  const navigate = useNavigate();

  // Smart Scenario Setup — Supports BOTH old and new formats
  useEffect(() => {
    let initScenario = null;

    if (scenarios && Array.isArray(scenarios) && scenarios.length > 0) {
      const first = scenarios[0];

      // CASE 1: Old format — full scenario object with .scenes
      if (first.scenes && typeof first.scenes === 'object') {
        initScenario = first;
      }

      // CASE 2: New flat array format — convert to scene graph
      else if (first.line !== undefined || first.speaker !== undefined) {
        console.log('WhatsAppSimulator: Building dynamic scenario from flat steps');

        const scenes = {};
        let initialSceneId = null;

        // Build scenes from flat steps
        scenarios.forEach((step, index) => {
          const sceneId = step.sceneId || `msg_${index}`;
          if (!initialSceneId) initialSceneId = sceneId;

          scenes[sceneId] = {
            speaker: step.speaker || 'Unknown Sender',
            profilePic: step.profilePic || '',
            line: step.line || '',
            choices: step.choices || [], // Use if provided
          };
        });

        const sceneIds = Object.keys(scenes);

        // Auto-link non-terminal scenes
        sceneIds.forEach((sceneId, idx) => {
          const scene = scenes[sceneId];

          // Skip if already has choices (backend-provided)
          if (scene.choices.length > 0) return;

          // Auto-generate choices for non-final scenes
          if (idx < sceneIds.length - 1) {
            const nextId = sceneIds[idx + 1];
            scene.choices = [
              { text: "This looks suspicious. I'm reporting and blocking.", nextSceneId: 'safe_block' },
              { text: "Okay, what should I do?", nextSceneId: nextId }
            ];
          }
        });

        // Add universal safe end state
        scenes['safe_block'] = {
          speaker: 'You',
          profilePic: 'You',
          line: "I'm blocking and reporting this number.",
          isEndState: true,
          isCorrect: true,
          explanation: "Excellent! Never engage with suspicious messages. Block and report immediately.",
          indicators: ["Unsolicited message", "Urgency", "Request for codes/links", "Unknown sender"]
        };

        // Mark last scene as end if not already terminal
        const lastId = sceneIds[sceneIds.length - 1];
        if (!scenes[lastId].isEndState) {
          scenes[lastId] = {
            ...scenes[lastId],
            isEndState: true,
            isCorrect: false,
            explanation: "You continued engaging with a suspicious sender. Legitimate organizations never ask for codes or payments via WhatsApp."
          };
        }

        initScenario = {
          title: challenge?.title || 'WhatsApp Phishing Simulation',
          description: challenge?.description || 'Handle a suspicious message safely',
          initialSceneId,
          arenaId: challenge?.arenaId || 'phishing_social_engineering',
          scenes
        };
      }
    }

    // Final fallback
    if (!initScenario) {
      initScenario = getDefaultScenario();
    }

    setScenario(initScenario);
    setCurrentScene(initScenario.scenes[initScenario.initialSceneId]);

    // Reset state
    setHistory([]);
    setIsComplete(false);
    setFinalFeedback(null);
    setShowFeedbackModal(false);
    setView('chatList');
  }, [scenarios, challenge]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  const handleSelectChat = () => {
    setView('chatView');
    setHistory([
      { type: 'system', line: 'Today' },
      { type: 'attacker', speaker: currentScene.speaker, line: currentScene.line, profilePic: currentScene.profilePic }
    ]);
  };

  const handleChoice = (choice) => {
    if (isComplete) return;

    const nextScene = scenario.scenes[choice.nextSceneId];
    if (!nextScene) return;

    const newEntry = { type: 'user', speaker: 'You', line: choice.text };
    const attackerEntry = { type: 'attacker', speaker: nextScene.speaker, line: nextScene.line, profilePic: nextScene.profilePic };

    setHistory(h => [...h, newEntry, attackerEntry]);
    setCurrentScene(nextScene);

    if (nextScene.isEndState) {
      setIsComplete(true);
      const feedback = {
        isCorrect: nextScene.isCorrect,
        explanation: nextScene.explanation,
        indicators: nextScene.indicators || []
      };
      setFinalFeedback(feedback);
      setShowFeedbackModal(true);

      onComplete?.({
        correct: nextScene.isCorrect ? 1 : 0,
        incorrect: nextScene.isCorrect ? 0 : 1,
        total: 1
      });
    }
  };

  const handleCloseFeedbackModal = () => {
    setShowFeedbackModal(false);
  };

  const handleLearnMore = () => {
    const meta = challenge?.metadata;

    // Support older challenges where metadata was a plain URL string
    if (typeof meta === 'string' && meta.startsWith('http')) {
      window.open(meta, '_blank', 'noopener,noreferrer');
      return;
    }

    // Support newer JSON metadata objects with explicit URLs
    if (meta && typeof meta === 'object') {
      const url =
        meta.learnMoreUrl ||
        meta.learn_more_url ||
        meta.docUrl ||
        meta.doc_url ||
        meta.url;

      if (typeof url === 'string' && url.startsWith('http')) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
    }

    // Fallback to arena-based navigation
    const arenaId = scenario?.arenaId || 'phishing_social_engineering';
    navigate(`/learning/module/${arenaId}?topic=${arenaId}`);
  };

  const fakeChats = [
    { name: 'John Doe', message: 'See you tomorrow!', time: '10:48 AM', unread: 0, profilePic: 'Man' },
    { name: 'Mom', message: 'Can you pick up groceries?', time: '9:15 AM', unread: 0, profilePic: 'Woman and Child' },
    { name: 'Project Group', message: 'Alice: I finished my part.', time: 'Yesterday', unread: 0, profilePic: 'Group' },
  ];

  const renderChatList = () => (
    <div className="flex flex-col h-full bg-gray-100">
      <header className="bg-[#008069] text-white p-4 flex justify-between items-center shadow-md">
        <h1 className="text-xl font-semibold">WhatsApp</h1>
        <div className="flex items-center space-x-4">
          <Search size={20} />
          <MoreVertical size={20} />
        </div>
      </header>
      <div className="flex-1 overflow-y-auto">
        <div
          className="flex items-center p-3 cursor-pointer bg-white animate-pulse-bg border-b border-gray-200"
          onClick={handleSelectChat}
        >
          <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center text-2xl mr-4">
            {currentScene?.profilePic || ''}
          </div>
          <div className="flex-1">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-gray-900">{currentScene?.speaker || 'Unknown'}</h3>
              <p className="text-xs text-green-500 font-semibold">3:57 PM</p>
            </div>
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600 truncate">{currentScene?.line || '...'}</p>
              <span className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold">1</span>
            </div>
          </div>
        </div>
        {fakeChats.map((chat, i) => (
          <div key={i} className="flex items-center p-3 border-b border-gray-200 filter blur-[2px] opacity-60">
            <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center text-2xl mr-4">
              {chat.profilePic}
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-center">
                <h3 className="font-medium text-gray-700">{chat.name}</h3>
                <p className="text-xs text-gray-500">{chat.time}</p>
              </div>
              <p className="text-sm text-gray-500">{chat.message}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderChatView = () => (
    <div className="flex flex-col h-full bg-[#ECE5DD]">
      <style>{`.bg-whatsapp-pattern { background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 800 800"><g fill="%23a1a1a1" fill-opacity="0.1"><path d="M400 0L0 400h200v200L400 400l200 200V400h200L400 0zM400 800l-200-200h200V400h200v200h200L400 800z"/></g></svg>'); }`}</style>
      <header className="bg-[#008069] text-white p-3 flex items-center shadow-md z-10">
        <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-2xl mr-3">
          {currentScene?.profilePic || ''}
        </div>
        <h2 className="text-lg font-semibold flex-1">{currentScene?.speaker || 'Unknown'}</h2>
        <div className="flex items-center space-x-4">
          <MoreVertical size={24} />
        </div>
      </header>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-whatsapp-pattern">
        {history.map((entry, i) => {
          if (entry.type === 'system')
            return <div key={i} className="text-center my-2"><span className="bg-[#E1F2FB] text-gray-700 text-xs font-semibold px-2 py-1 rounded-md shadow-sm">{entry.line}</span></div>;

          const isUser = entry.type === 'user';
          return (
            <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-xs md:max-w-sm p-2 px-3 rounded-lg shadow-sm text-gray-800 ${isUser ? 'bg-[#DCF8C6]' : 'bg-white'}`}>
                <p className="text-base">{entry.line}</p>
                {isUser && (
                  <div className="flex justify-end items-center mt-1">
                    <span className="text-xs text-gray-500 mr-1">3:58 PM</span>
                    <Check size={16} className="text-blue-500" />
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </div>
      <footer className="p-2 bg-[#ECE5DD] border-t border-gray-300">
        {isComplete ? (
          <div className={`flex items-center gap-3 p-3 rounded-lg text-sm ${finalFeedback?.isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {finalFeedback?.isCorrect ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
            <span className="font-medium">This conversation has ended.</span>
          </div>
        ) : currentScene?.choices?.length > 0 ? (
          <div className="space-y-2">
            {currentScene.choices.map((c, i) => (
              <button
                key={i}
                onClick={() => handleChoice(c)}
                className="w-full text-center p-3 text-blue-600 font-medium bg-white hover:bg-gray-100 rounded-lg transition-colors text-sm shadow-sm border border-gray-200"
              >
                {c.text}
              </button>
            ))}
          </div>
        ) : null}
      </footer>
    </div>
  );

  const renderFeedbackModal = () => {
    if (!showFeedbackModal || !finalFeedback) return null;
    const { isCorrect, explanation, indicators } = finalFeedback;

    return (
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full p-8 m-4 animate-fade-in-up">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              {isCorrect ? <CheckCircle size={28} className="text-green-600" /> : <AlertTriangle size={28} className="text-red-600" />}
              <h2 className={`text-2xl font-bold ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                {isCorrect ? 'Correct Decision!' : 'Incorrect Decision!'}
              </h2>
            </div>
            <button onClick={handleCloseFeedbackModal} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
          </div>
          <div className="text-gray-700 space-y-4">
            <p className="text-base">{explanation}</p>
            {indicators?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h3 className="font-bold text-yellow-800 flex items-center">
                  <AlertTriangle size={16} className="mr-2" /> Key Indicators Found:
                </h3>
                <ul className="list-disc list-inside space-y-1.5 mt-2 text-gray-700 text-sm pl-2">
                  {indicators.map((ind, i) => <li key={i}>{ind}</li>)}
                </ul>
              </div>
            )}
          </div>
          <div className="mt-8 flex justify-end items-center gap-4">
            <button onClick={handleLearnMore} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors">
              <BookOpen size={16} /> Learn More
            </button>
            <button onClick={handleCloseFeedbackModal} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative h-[calc(100dvh-140px)] sm:h-[calc(100dvh-200px)] w-full max-w-xl mx-auto bg-white shadow-2xl overflow-hidden font-sans flex border border-gray-200 rounded-lg">
      <div className="h-full w-full">
        {view === 'chatList' ? renderChatList() : renderChatView()}
      </div>
      {renderFeedbackModal()}
    </div>
  );
};

export default WhatsAppSimulator;